import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Navigator from './components/Navigation';
import LifeTodoToggle from './components/LifeTodoToggle';
import ProtectedSection from './components/ProtectedSection';
import { hashPasswordsFromEnv } from './utils/passwordUtils';
import { useMetaTags } from './utils/useMetaTags';
import { supabase, DailyEntry } from './lib/supabaseClient';
import './font.css';
import './style.css';

/** "YYYY-MM-DD" → "yy년 mm월 dd일" */
const formatEntryDate = (iso: string): string => {
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const [yyyy, mm, dd] = parts;
  return `${yyyy.slice(-2)}년 ${mm}월 ${dd}일`;
};

/** 사용자 로컬 기준 오늘 날짜 (YYYY-MM-DD).
 *  DB의 default current_date는 UTC라 KST 새벽 시간엔 전날로 저장되므로 직접 보낸다. */
const localToday = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** 1~6 점수에 대응되는 라벨. index 0은 미사용. */
const RATING_LABELS: readonly string[] = [
  '',
  '아주나쁨',
  '나쁨',
  '별로',
  '괜찮아',
  '좋음',
  '아주좋음',
];
const ratingLabel = (n: number): string => RATING_LABELS[n] ?? String(n);

/** 1~6 점수에 따른 모달 배경색. index 0은 미사용. */
const RATING_BG_COLORS: readonly string[] = [
  '',
  '#E6DCFF',
  '#DADBFF',
  '#DBF5FD',
  '#E6F5BB',
  '#FFF2C5',
  '#FDDCCD',
];
const ratingBgColor = (n: number): string => RATING_BG_COLORS[n] ?? '#ffffff';

/** 1~6 점수에 따른 슬라이더 핸들(thumb) 색상. index 0은 미사용. */
const RATING_THUMB_COLORS: readonly string[] = [
  '',
  '#2D177C',
  '#4F51E9',
  '#3B9FB9',
  '#569400',
  '#E5BD28',
  '#FC7631',
];
const ratingThumbColor = (n: number): string => RATING_THUMB_COLORS[n] ?? '#131517';

/** 1~6 점수에 따른 타이틀(페이지 타이틀, 모달 상태값) 색상. */
const RATING_TITLE_COLORS: readonly string[] = [
  '',
  '#1D104A',
  '#0E0F45',
  '#072B34',
  '#1D2E06',
  '#3C3109',
  '#522209',
];
const ratingTitleColor = (n: number): string => RATING_TITLE_COLORS[n] ?? '#131517';

function PageDaily(props: {
  activeNav: number;
  setActiveNav: Dispatch<SetStateAction<number>>;
}) {
  useMetaTags({
    title: 'Daily - Lynkim',
    description: 'Daily',
    image: '/ogimage.png',
  });

  return (
    <div>
      <Navigator activeNav={props.activeNav} setActiveNav={props.setActiveNav} />
      <div
        className='pageTitle'
        style={{
          color: 'var(--daily-title-color, var(--color-text-primary))',
          opacity: 0.85,
          transition: 'color 0.4s ease',
        }}
      >
        Daily
      </div>
      <DailyBoard />
      <LifeTodoToggle />
    </div>
  );
}

function DailyBoard() {
  const [entries, setEntries] = useState<DailyEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  // ProtectedSection 잠금 해제 여부 — sessionStorage 기반, 같은 탭에선 페이지 이동해도 유지
  const [unlocked, setUnlocked] = useState<boolean>(
    () =>
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem('protectedSection.daily') === '1'
  );

  // 가장 많이 등장하는 rating 값 (동률이면 가장 최근에 기록된 것을 우선).
  // entries는 fetch 시 entry_date desc, created_at desc 로 정렬되어 있으므로
  // 동률 중에서는 entries[0] 쪽에 가까운 항목이 더 최신이다.
  const dominantRating = useMemo<number | null>(() => {
    if (entries.length === 0) return null;
    const counts = new Array(7).fill(0) as number[]; // index 1~6 사용
    for (const e of entries) {
      if (e.rating >= 1 && e.rating <= 6) counts[e.rating]++;
    }
    let max = 0;
    for (let r = 1; r <= 6; r++) {
      if (counts[r] > max) max = counts[r];
    }
    if (max === 0) return null;
    // 최신순으로 순회하며 max와 같은 rating이 나오면 그 값을 반환
    for (const e of entries) {
      if (e.rating >= 1 && e.rating <= 6 && counts[e.rating] === max) {
        return e.rating;
      }
    }
    return null;
  }, [entries]);

  // 페이지(body) 배경색을 dominantRating의 모달 배경색으로 적용
  useEffect(() => {
    if (dominantRating == null) return;
    const body = document.body;
    const prev = body.style.backgroundColor;
    body.style.backgroundColor = ratingBgColor(dominantRating);
    return () => {
      body.style.backgroundColor = prev;
    };
  }, [dominantRating]);

  // Daily 토글 active 색 + 페이지 타이틀 색을 dominantRating 기반으로 적용 (CSS 변수로 전달)
  useEffect(() => {
    if (dominantRating == null) return;
    const root = document.documentElement;
    const prevActive = root.style.getPropertyValue('--daily-active-color');
    const prevTitle = root.style.getPropertyValue('--daily-title-color');
    root.style.setProperty('--daily-active-color', ratingThumbColor(dominantRating));
    root.style.setProperty('--daily-title-color', ratingTitleColor(dominantRating));
    return () => {
      if (prevActive) root.style.setProperty('--daily-active-color', prevActive);
      else root.style.removeProperty('--daily-active-color');
      if (prevTitle) root.style.setProperty('--daily-title-color', prevTitle);
      else root.style.removeProperty('--daily-title-color');
    };
  }, [dominantRating]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: err } = await supabase
        .from('daily_entries')
        .select('*')
        .order('entry_date', { ascending: false })
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (err) setError(err.message);
      setEntries((data ?? []) as DailyEntry[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCreate = useCallback(
    async (content: string, rating: number) => {
      const nextPosition =
        entries.length > 0 ? Math.max(...entries.map((e) => e.position)) + 1 : 0;
      const { data, error: err } = await supabase
        .from('daily_entries')
        .insert({ content, rating, position: nextPosition, entry_date: localToday() })
        .select()
        .single();
      if (err) {
        setError(err.message);
        return;
      }
      if (data) {
        setEntries((prev) => [data as DailyEntry, ...prev]);
      }
    },
    [entries]
  );

  const handleContentChange = useCallback((id: string, content: string) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, content } : e)));
  }, []);

  const handleContentCommit = useCallback(async (id: string, content: string) => {
    const { error: err } = await supabase
      .from('daily_entries')
      .update({ content, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) setError(err.message);
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    if (!window.confirm('이 항목을 삭제할까요?')) return;
    setEntries((prev) => prev.filter((e) => e.id !== id));
    const { error: err } = await supabase.from('daily_entries').delete().eq('id', id);
    if (err) setError(err.message);
  }, []);

  if (loading) {
    return <div className='todoBoard' />;
  }

  return (
    <div className='todoBoard'>
      {error && (
        <div
          style={{
            color: 'var(--color-danger)',
            background: 'var(--color-danger-soft)',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      <DailyRatingChart entries={entries} />

      <div style={{ position: 'relative', zIndex: 3 }}>
        <ProtectedSection
          lockPassword={hashPasswordsFromEnv(process.env.REACT_APP_DAILY_SECTION_PASSWORDS)}
          description="이 영역의 기록을 보려면 비밀번호가 필요해요"
          persistKey='daily'
          onUnlock={() => setUnlocked(true)}
        >
          <div className='todoGrid'>
            {entries.map((entry) => (
              <DailyCard
                key={entry.id}
                entry={entry}
                onContentChange={(content) => handleContentChange(entry.id, content)}
                onContentCommit={(content) => handleContentCommit(entry.id, content)}
                onDelete={() => handleDelete(entry.id)}
              />
            ))}
          </div>
        </ProtectedSection>
      </div>

      {unlocked && (
        <>
          <button className='dailyFloatingBtn' onClick={() => setModalOpen(true)}>
            + 기록하기
          </button>

          <NewDailyModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            onSubmit={async (text, rating) => {
              await handleCreate(text, rating);
              setModalOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
}

/** 최근 14일 1~6 점수의 시계열 라인 차트 (페이지 width 100% × 200px 높이) */
function DailyRatingChart({ entries }: { entries: DailyEntry[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const pathRef = useRef<SVGPathElement | null>(null);
  const [pathLength, setPathLength] = useState(0);

  // 컨테이너 너비를 ResizeObserver로 추적
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setWidth(el.getBoundingClientRect().width);
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  // path가 그려진 후 총 길이를 측정 → stroke-dashoffset 애니메이션에 사용.
  // 초기 마운트 + 너비 변경 시에만 측정 (bend 인터랙션 중에는 측정 X)
  useEffect(() => {
    if (pathRef.current) {
      setPathLength(pathRef.current.getTotalLength());
    }
  }, [width]);

  // 진입 애니메이션이 끝나는 시점을 추적 — 끝난 후에만 bend 인터랙션 활성화
  const [animationDone, setAnimationDone] = useState(false);
  useEffect(() => {
    if (pathLength > 0 && !animationDone) {
      const t = setTimeout(() => setAnimationDone(true), 1300);
      return () => clearTimeout(t);
    }
  }, [pathLength, animationDone]);

  // 마우스 위치 기반 휘어짐(bend) 인터랙션
  const [bend, setBend] = useState<{ x: number; y: number; strength: number } | null>(null);
  const decayRafRef = useRef<number | null>(null);

  const cancelDecay = useCallback(() => {
    if (decayRafRef.current != null) {
      cancelAnimationFrame(decayRafRef.current);
      decayRafRef.current = null;
    }
  }, []);

  useEffect(() => () => cancelDecay(), [cancelDecay]);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!animationDone) return;
    cancelDecay();
    const rect = e.currentTarget.getBoundingClientRect();
    setBend({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      strength: 1,
    });
  };

  const handleMouseLeave = () => {
    cancelDecay();
    setBend((current) => {
      if (!current) return null;
      const initial = current;
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min((now - start) / 800, 1);
        if (t >= 1) {
          setBend(null);
          decayRafRef.current = null;
        } else {
          setBend({ ...initial, strength: initial.strength * (1 - t) });
          decayRafRef.current = requestAnimationFrame(tick);
        }
      };
      decayRafRef.current = requestAnimationFrame(tick);
      return current;
    });
  };

  // 오늘로부터 과거 14일 + 미래 2일 = 총 16일.
  // 오늘이 fade 영역에 걸리지 않도록 오른쪽으로 2일 연장.
  const days = useMemo(() => {
    const fmt = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    const arr: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 13; i >= -2; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      arr.push(fmt(d));
    }
    return arr;
  }, []);

  // 날짜별 rating (같은 날 여러 기록이면 최신 우선 — entries가 desc 정렬 상태)
  const ratingByDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of entries) {
      if (!m.has(e.entry_date)) m.set(e.entry_date, e.rating);
    }
    return m;
  }, [entries]);

  // 점수 없는 날은 기본 4로 간주해서 곡선이 끊기지 않도록 한다.
  const points = useMemo(
    () =>
      days.map((date, i) => ({
        x: i,
        y: ratingByDate.get(date) ?? 4,
        date,
        hasRating: ratingByDate.has(date),
      })),
    [days, ratingByDate]
  );

  const height = 140;
  // 그림자/곡선이 SVG 박스 밖으로 흘러도 잘리지 않도록 위·아래로 여유 공간을 둠.
  // SVG 자체는 (height + SHADOW_PAD*2) 만큼 크지만, negative margin으로 레이아웃 점유 높이는 height 그대로.
  const SHADOW_PAD = 80;
  const svgHeight = height + SHADOW_PAD * 2;
  const padX = 0;
  const padY = 12;
  const innerW = Math.max(width - padX * 2, 0);
  const innerH = height - padY * 2;
  const lastIdx = Math.max(days.length - 1, 1);
  const xAt = (i: number) => padX + (innerW * i) / lastIdx;
  const yAt = (r: number) => SHADOW_PAD + padY + (1 - (r - 1) / 5) * innerH;

  // Catmull-Rom → cubic Bezier 변환으로 부드러운 곡선 path 생성.
  // TENSION 값이 클수록 곡선이 더 휘어져 부드러워진다.
  //  - 0.167 (≈ 1/6): 표준 Catmull-Rom
  //  - 0.25 ~ 0.35: 더 부드럽고 둥근 곡선
  //  - 0.5 이상: 오버슈트가 커져 출렁임이 강해짐
  const TENSION = 0.16;
  // bend가 활성일 때, 마우스 인근의 점을 마우스 위치 쪽으로 살짝 당긴다.
  const BEND_RADIUS = 140; // px — 영향 반경
  const BEND_PULL = 0.45;  // 0~1 — 최대 당김 강도
  const path = (() => {
    const pts = points.map((p) => {
      const basePx = xAt(p.x);
      const basePy = yAt(p.y);
      if (!bend || bend.strength <= 0) return { px: basePx, py: basePy };
      const dist = Math.abs(basePx - bend.x);
      if (dist >= BEND_RADIUS) return { px: basePx, py: basePy };
      const factor = (1 - dist / BEND_RADIUS) * bend.strength;
      return {
        px: basePx,
        py: basePy + (bend.y - basePy) * factor * BEND_PULL,
      };
    });
    if (pts.length === 0) return '';
    if (pts.length === 1) return `M${pts[0].px.toFixed(1)} ${pts[0].py.toFixed(1)}`;
    let d = `M${pts[0].px.toFixed(1)} ${pts[0].py.toFixed(1)}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(i - 1, 0)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(i + 2, pts.length - 1)];
      const cp1x = p1.px + (p2.px - p0.px) * TENSION;
      const cp1y = p1.py + (p2.py - p0.py) * TENSION;
      const cp2x = p2.px - (p3.px - p1.px) * TENSION;
      const cp2y = p2.py - (p3.py - p1.py) * TENSION;
      d += ` C${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.px.toFixed(1)} ${p2.py.toFixed(1)}`;
    }
    return d;
  })();

  return (
    <div
      ref={containerRef}
      style={{
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginTop: -60,
        height,
        marginBottom: 12,
        position: 'relative',
        zIndex: 2,
      }}
      aria-label='최근 14일 점수 추이'
    >
      {width > 0 && (() => {
        const ratedPoints = points.filter((p) => p.hasRating);
        const stroke =
          ratedPoints.length >= 2
            ? 'url(#dailyRatingGradient)'
            : ratedPoints.length === 1
            ? ratingThumbColor(ratedPoints[0].y)
            : 'var(--color-text-primary)';
        const startX = ratedPoints.length >= 2 ? xAt(ratedPoints[0].x) : 0;
        const endX =
          ratedPoints.length >= 2 ? xAt(ratedPoints[ratedPoints.length - 1].x) : 1;
        const range = Math.max(endX - startX, 1);

        return (
          <svg
            width={width}
            height={svgHeight}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
              display: 'block',
              opacity: 0.5,
              overflow: 'visible',
              // SVG는 height + SHADOW_PAD*2 만큼 크지만, 위·아래 negative margin으로 레이아웃 점유는 height 그대로.
              marginTop: -SHADOW_PAD,
              marginBottom: -SHADOW_PAD,
              // drop-shadow는 spread가 없어 같은 그림자를 여러 겹 stack해야 진하게 보임.
              // SVG opacity 0.5가 걸려있어 실제 시각 강도는 여기 % 값의 절반.
              filter: [
                'drop-shadow(0 0 20px color-mix(in srgb, var(--daily-active-color, var(--color-text-primary)) 90%, transparent))',
                'drop-shadow(0 0 48px color-mix(in srgb, var(--daily-active-color, var(--color-text-primary)) 60%, transparent))',
                'drop-shadow(0 0 80px color-mix(in srgb, var(--daily-active-color, var(--color-text-primary)) 20%, transparent))',
              ].join(' '),
              maskImage:
                'linear-gradient(to right, transparent 0%, black 20%, black 95%, transparent 100%)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent 0%, black 0%, black 98%, transparent 100%)',
            }}
          >
            {ratedPoints.length >= 2 && (
              <defs>
                <linearGradient
                  id='dailyRatingGradient'
                  x1={startX}
                  y1={0}
                  x2={endX}
                  y2={0}
                  gradientUnits='userSpaceOnUse'
                >
                  {ratedPoints.map((p) => {
                    const offset = ((xAt(p.x) - startX) / range) * 100;
                    return (
                      <stop
                        key={p.date}
                        offset={`${offset.toFixed(2)}%`}
                        stopColor={ratingThumbColor(p.y)}
                      />
                    );
                  })}
                </linearGradient>
              </defs>
            )}
            {points.length > 1 && (
              <path
                ref={pathRef}
                d={path}
                stroke={stroke}
                strokeWidth={5}
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
                style={
                  !animationDone && pathLength > 0
                    ? {
                        strokeDasharray: pathLength,
                        strokeDashoffset: pathLength,
                        animation: 'dailyChartDraw 1.2s ease-out forwards',
                      }
                    : undefined
                }
              />
            )}
          </svg>
        );
      })()}
    </div>
  );
}

function NewDailyModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (text: string, rating: number) => Promise<void> | void;
}) {
  const { isOpen, onClose, onSubmit } = props;
  const [text, setText] = useState('');
  const [rating, setRating] = useState<number>(4);
  const [isVisible, setIsVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // 모달 마운트/언마운트 트랜지션 — PasswordModal과 동일한 패턴
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setText('');
      setRating(4);
      // 페인트 후 포커스
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setIsVisible(false), 500);
    return () => clearTimeout(t);
  }, [isOpen]);

  if (!isVisible) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed, rating);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className={`password-modal-overlay password-modal-overlay--section ${isOpen ? 'modal-open' : 'modal-closing'}`}
      onMouseDown={handleOverlayMouseDown}
      data-theme='light'
    >
      <div
        className={`password-modal-container password-modal-container--section dailyModal ${isOpen ? 'modal-open' : 'modal-closing'}`}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          backgroundColor: ratingBgColor(rating),
          transition:
            'background-color 0.4s ease, opacity 0.8s ease-out, transform 0.5s ease-out',
        }}
      >
        <div className='password-modal-title'>오늘 기록하기</div>
        <form className='password-modal-form' onSubmit={handleSubmit}>
          <div className='dailyRatingField'>
            <div
              className='dailyRatingValue'
              style={{ color: ratingTitleColor(rating), transition: 'color 0.4s ease' }}
            >
              {ratingLabel(rating)}
            </div>
            <input
              type='range'
              min={1}
              max={6}
              step={1}
              value={rating}
              onChange={(e) => setRating(Number(e.target.value))}
              className='dailyRatingSlider'
              aria-label='오늘의 점수'
              style={{ ['--thumb-color' as any]: ratingThumbColor(rating) }}
            />
          </div>
          <input
            ref={inputRef}
            type='text'
            className='password-modal-input'
            value={text}
            placeholder='하루 남기기'
            onChange={(e) => setText(e.target.value)}
          />
          <div className='password-modal-buttons'>
            <button
              type='button'
              className='password-modal-button password-modal-button-secondary'
              onClick={onClose}
              disabled={submitting}
            >
              닫기
            </button>
            <button
              type='submit'
              className='password-modal-button password-modal-button-primary dailyKeepBtn'
              disabled={!text.trim() || submitting}
              style={{
                backgroundColor: ratingThumbColor(rating),
                color: '#fff',
                borderColor: 'transparent',
              }}
            >
              남기기
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DailyCard(props: {
  entry: DailyEntry;
  onContentChange: (content: string) => void;
  onContentCommit: (content: string) => void;
  onDelete: () => void;
}) {
  const { entry, onContentChange, onContentCommit, onDelete } = props;
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(
    () => () => {
      if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    },
    []
  );

  // textarea 높이를 콘텐츠에 맞춰 자동 조정 — 카드 높이가 글자 길이 + padding으로 결정되도록
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // 초기 마운트 및 entry.content 변경 시 높이 재계산
  useEffect(() => {
    autoResize();
  }, [entry.content, autoResize]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    onContentChange(v);
    autoResize();
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => onContentCommit(v), 500);
  };

  return (
    <div
      className='dailyCard fadeInUp'
    >
      <div className='dailyCardHeader'>
        <div className='dailyCardTitleGroup'>
          <span className='dailyCardDate'>{formatEntryDate(entry.entry_date)}</span>
          <span
            className='dailyCardRating'
            style={{ color: ratingThumbColor(entry.rating) }}
          >
            {ratingLabel(entry.rating)}
          </span>
        </div>
        <button
          className='dailyCardDelete'
          onClick={onDelete}
          aria-label='항목 삭제'
        >
          ✕
        </button>
      </div>

      <textarea
        ref={textareaRef}
        className='dailyCardText'
        value={entry.content}
        placeholder='오늘 한 줄'
        onChange={handleChange}
        onBlur={(e) => onContentCommit(e.target.value)}
        rows={1}
      />
    </div>
  );
}

export default PageDaily;
